<template>
  <div :id="id" :class="className" :style="{height:height,width:width}"/>
</template>

<script>
import echarts from 'echarts'
import resize from "@/components/Charts/mixins/resize";
import { ls3ChartFont } from '@/utils/ls3ChartCompact'


export default {
  mixins: [resize],
  props: {
    className: {
      type: String,
      default: 'chart'
    },
    id: {
      type: String,
      default: 'chart'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    path: {
      type: String

    },
    show_mode: {
      type: String,
      default: 'complex'
    },
    chart_type: {
      type: String,
      default: "bar"
    },
    area_color: {
      type: String,
      default: "#6DF4BA"
    },
    params: {},
    /**
     * Y 轴刻度文字字号基准（px），再经 ls3ChartFont 随大屏 V3 缩放；默认 20 与其它轴一致。
     */
    yAxisLabelBasePx: {
      type: Number,
      default: 20
    },
    /** 横轴类目较多（如省区名）时增大 grid 底部并倾斜标签，避免裁切 */
    expandBottomAxis: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chart: null,
    }
  },
  mounted() {
    this.initChart()

  },
  beforeDestroy() {
    if (!this.chart) {
      return
    }
    this.chart.dispose()
    this.chart = null
  },
  watch: {
    '$route': function (to, from) {
      if (to != from) {
        this.initChart();
      }
    }
  },
  methods: {
    initChart() {
      this.chart = echarts.init(document.getElementById(this.id), 'dark')
      const f = ls3ChartFont
      const primaryColor = this.area_color || '#6DF4BA'
      const axisLbl = {
        fontSize: f(15),
        color: 'rgba(233, 246, 255, 0.9)',
        margin: this.expandBottomAxis ? 10 : 8,
        ...(this.expandBottomAxis ? { rotate: 20, interval: 0 } : {})
      }

      this.chart.setOption({
        backgroundColor: this.show_mode === "complex" ? 'transparent' : "#42444F",
        toolbox: {
          show: this.show_mode === "complex" ? true : false,
          iconStyle: {
            borderColor: 'rgba(194, 224, 255, 0.9)'
          },
          emphasis: {
            iconStyle: {
              borderColor: '#7FD8FF'
            }
          },
          feature: {
            saveAsImage: {show: this.show_mode === "complex" ? true : false},
            myTool1: {
              show: this.show_mode === "complex" ? true : false,
              title: '全屏',
              icon: "path://M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z",
              onclick: () => {
                const element = document.getElementById(this.id);
                if (element.requestFullScreen) { // HTML W3C 提议
                  element.requestFullScreen();
                } else if (element.msRequestFullscreen) { // IE11
                  element.msRequestFullScreen();
                } else if (element.webkitRequestFullScreen) { // Webkit (works in Safari5.1 and Chrome 15)
                  element.webkitRequestFullScreen();
                } else if (element.mozRequestFullScreen) { // Firefox (works in nightly)
                  element.mozRequestFullScreen();
                }
                // 退出全屏
                if (element.requestFullScreen) {
                  document.exitFullscreen();
                } else if (element.msRequestFullScreen) {
                  document.msExitFullscreen();
                } else if (element.webkitRequestFullScreen) {
                  document.webkitCancelFullScreen();
                } else if (element.mozRequestFullScreen) {
                  document.mozCancelFullScreen();
                }
              },
            }
          }
        },
        color: ['#62f5c2', '#4ec3ff', '#ffc86f', '#b18cff', '#7cf0ff', '#ff8fa3'],
        tooltip: {
          trigger: 'axis',
          show: true,
          backgroundColor: 'rgba(13, 18, 30, 0.9)',
          borderColor: 'rgba(110, 196, 255, 0.35)',
          borderWidth: 1,
          textStyle: {
            color: '#e7f5ff',
            fontSize: f(14)
          },
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(103, 188, 255, 0.12)'
            }
          }
        },
        legend: {
          data: [],
          top: 6,
          itemWidth: 14,
          itemHeight: 8,
          textStyle: {
            color: 'rgba(233, 246, 255, 0.82)'
          }
        },
        grid: {
          left: '2%',
          right: '2.6%',
          bottom: this.expandBottomAxis ? '15%' : '2%',
          containLabel: this.show_mode === "complex" ? true : false,
          top: this.show_mode === "complex" ? 42 : -1,
        },
        dataZoom: [
          {
            show: this.show_mode === "complex" ? true : false,
            realtime: this.show_mode === "complex" ? true : false,
            height: 14,
            borderColor: 'rgba(145, 200, 255, 0.35)',
            textStyle: {
              color: 'rgba(218, 239, 255, 0.75)',
              fontSize: f(11)
            },
            fillerColor: 'rgba(98, 245, 194, 0.22)',
            backgroundColor: 'rgba(16, 24, 38, 0.82)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(117, 184, 255, 0.7)'
              },
              areaStyle: {
                color: 'rgba(117, 184, 255, 0.2)'
              }
            },
            handleStyle: {
              color: 'rgba(201, 231, 255, 0.95)',
              borderColor: 'rgba(103, 188, 255, 0.95)'
            }
          },
          {
            type: 'inside',
            realtime: this.show_mode === "complex" ? true : false,
          }
        ],
        xAxis: [
          {
            type: 'category',
            show: this.show_mode === "complex" ? true : false,
            boundaryGap: this.show_mode === "complex" ? true : false,
            axisLabel: axisLbl,
            axisLine: {
              lineStyle: {
                color: 'rgba(148, 197, 255, 0.55)',
                width: 1
              }
            },
            axisTick: {
              show: false
            },
            data: []
          }
        ],
        yAxis: [
          {
            type: 'value',
            show: this.show_mode === "complex" ? true : false,
            axisLabel: {
              fontSize: f(Math.min(this.yAxisLabelBasePx, 15)),
              color: 'rgba(220, 238, 255, 0.86)'
            },
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            boundaryGap: this.show_mode === "complex" ? true : false,
            splitLine: {
              show: this.show_mode === "complex" ? true : false,
              lineStyle: {
                color: ['rgba(136, 170, 210, 0.24)'],
                width: 1,
                type: 'dashed'
              }
            }
          }
        ],
        series: []
      })

      var post_params1 = {"chart_type": this.chart_type}
      var post_params = Object.assign({}, post_params1, {"extra_params": this.params});
      // console.log('that.show_mode11:' + this.show_mode)
      let that = this
      this.$http.post(process.env.VUE_APP_API_HOST + "/" + this.path, post_params, {"content-type": "application/json"}).then((res) => {
        // console.log('inside that.show_mode:ffff,' + that.show_mode)
        var series_data = []
        for (let x of res.data.data) {
          const seriesType = x.type || this.chart_type
          x['areaStyle'] = {
            opacity: 0.9,
            normal: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
                offset: 0,
                color: this.area_color,
              }, {
                offset: 1,
                color: 'rgba(0,0,0,0)'
              }]),
            }
          }
          x['barMaxWidth'] = 36
          x['itemStyle'] = {
            borderRadius: seriesType === 'bar' ? [8, 8, 0, 0] : 0,
            shadowBlur: seriesType === 'bar' ? 14 : 0,
            shadowColor: seriesType === 'bar' ? 'rgba(98, 245, 194, 0.25)' : 'transparent',
            color: seriesType === 'bar'
              ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
                offset: 0,
                color: primaryColor
              }, {
                offset: 1,
                color: 'rgba(98, 245, 194, 0.72)'
              }])
              : undefined
          }
          if (seriesType === 'line') {
            x['smooth'] = true
            x['symbol'] = 'circle'
            x['symbolSize'] = 7
            x['lineStyle'] = {
              width: 3,
              shadowBlur: 8,
              shadowColor: 'rgba(102, 205, 255, 0.3)'
            }
          }
          x['smooth'] = this.show_mode === "complex" ? true : false
          if (that.show_mode == "complex") {
            // console.log('that.show_mode:ffff,' + that.show_mode)
            if (x['markLine']) {
              x['markLine'] = {
                data: [{type: 'average', name: 'Avg'}]
              }
              x['markPoint'] = {
                data: [
                  {type: 'max', name: 'Max'},
                  {type: 'min', name: 'Min'}
                ]
              }
            }
          } else {
            x['markLine'] = {}
            x['markPoint'] = {}
          }
          x['label'] = {'show': false, "fontSize": f(14)}
          series_data.push(x)
        }
        this.chart.setOption({
          xAxis: [
            {
              type: 'category',
              data: res.data.dates,
              boundaryGap: this.show_mode === "complex" ? true : false,
              axisLabel: {
                fontSize: f(15),
                ...(that.expandBottomAxis ? { rotate: 20, interval: 0 } : {})
              }
            }
          ],
          // yAxis:{
          //   type: 'value',
          //   splitLine:false
          // },
          legend: {
            textStyle: {
              fontSize: f(14),
              color: 'rgba(233, 246, 255, 0.82)'
            },
            data: res.data.legend,
            show: that.show_mode === "complex" ? true : false
          },
          series: series_data
        })
        // console.log("my options:"+that.chart.options())
      })
    }
  }
}
</script>
